import { supabase } from './client';
import { notifyFriendInvite } from '@/utils/notificationUtils';

/**
 * Create (or re-send) an invite. Upserts on (inviter_id, email).
 */
export const createInvite = async (
  email: string,
  options?: { manualPlayerId?: string }
): Promise<{ error?: string }> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Authentication required' };

  const inviteType = options?.manualPlayerId ? 'manual_player' : 'new_user';

  const { error } = await supabase
    .from('invites')
    .upsert(
      {
        inviter_id: user.id,
        email: email.toLowerCase().trim(),
        manual_player_id: options?.manualPlayerId || null,
        invite_type: inviteType,
        status: 'pending',
      },
      { onConflict: 'inviter_id,email' }
    );

  if (error) {
    console.error('Error creating invite:', error);
    return { error: error.message };
  }

  // Fire-and-forget email
  notifyFriendInvite({
    type: inviteType,
    recipientEmail: email.toLowerCase().trim(),
  });

  return {};
};

/**
 * Fetch pending invites addressed to the current user's email.
 */
export const getPendingInvitesForEmail = async () => {
  const { data, error } = await supabase
    .from('invites')
    .select('id, inviter_id, email, manual_player_id, invite_type, status')
    .eq('status', 'pending');

  if (error) {
    console.error('Error fetching pending invites:', error);
    return [];
  }

  // RLS filters to invites matching the user's email
  return data || [];
};

/**
 * Mark an invite as accepted.
 */
export const acceptInvite = async (inviteId: string): Promise<{ error?: string }> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Authentication required' };

  const { error } = await supabase
    .from('invites')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
      accepted_by: user.id,
    })
    .eq('id', inviteId);

  if (error) {
    console.error('Error accepting invite:', error);
    return { error: error.message };
  }

  return {};
};

/**
 * Check if the current user has already invited this email.
 */
export const hasExistingInvite = async (email: string): Promise<boolean> => {
  const { data } = await supabase
    .from('invites')
    .select('id')
    .eq('email', email.toLowerCase().trim())
    .limit(1);

  return (data?.length ?? 0) > 0;
};
